import React, { useEffect, useMemo, useState } from 'react';
import './monitoringSettings.scss';

// Client-facing configurator for which report changes are monitored and how important each one is.
// Two panes: a left rail of report types (with monitored counts + search), and a main pane showing the
// selected report's changes. Most changes come in a worse/better pair (e.g. "Liens increased" /
// "Liens decreased"); the adverse direction is the primary row and the benign direction is a dimmed,
// opt-in sub-row beneath it. Background (Lexis) has many changes, so they're grouped into categories.
//  - Signals: GET/PUT /applications/monitoring/severity-config (signal -> severity; OFF = not monitored).
//  - Fields ("More fields"): GET/PUT /applications/monitoring/watched-fields against a per-report catalog.

const IMPORTANCE = ['ADVERSE', 'WARNING', 'INFORMATIONAL'];

// Report-type enum -> display group (authoritative; backend sends reportType with each signal/field).
const REPORT_GROUP = {
    FMCSA: 'FMCSA', SOS: 'Secretary of State',
    LEXIS_VERIFICATION: 'Verification (Lexis)', LEXIS_BACKGROUND: 'Background (Lexis)', UCC: 'UCC search',
};
const GROUP_ORDER = [
    'FMCSA', 'Secretary of State', 'Background (Lexis)', 'Verification (Lexis)', 'UCC search', 'Other',
];
// Legacy fallback grouping when reportType is absent.
const groupOfSignal = (signal) => {
    if (signal.startsWith('SOS')) return 'Secretary of State';
    if (signal.startsWith('FMCSA')) return 'FMCSA';
    if (signal.startsWith('VERIFICATION')) return 'Verification (Lexis)';
    if (signal.startsWith('UCC_FILING')) return 'UCC search';
    if (/^(LIENS|UCC_|BANKRUPTCY|CRIMINAL|RECORDS)/.test(signal)) return 'Background (Lexis)';
    return 'Other';
};

// Per-report sub-categories, matched by signal-name pattern. Reports absent here show a single section.
const bgCategory = (signal) => {
    if (/^(LIENS|UCC_|BANKRUPTCY|FORECLOSURE|CIVIL_COURT|NOTICE_OF_DEFAULT)/.test(signal)) return 'Financial distress';
    if (/^(CRIMINAL|SANCTION|WATCHLIST|SEX_OFFENSE|CONTROLLED_SUBSTANCE)/.test(signal)) return 'Criminal & sanctions';
    if (/^(RECORDS|INCORPORATION|BUSINESS_REGISTRATION|REGISTERED_AGENT|CONNECTED_BUSINESS|ASSOCIATE|LICENSE)/.test(signal)) return 'Corporate status';
    return 'Other changes';
};
const verCategory = (signal) => (/(WATCHLIST|RISK_FLAG|CMRA|RESIDENTIAL)/.test(signal) ? 'Risk & watchlists' : 'Identity verification');
const CATEGORIES = {
    // 'Other changes' catches any background signal not matched by bgCategory's three groups (incl. future
    // signals); empty categories are filtered out at render, so listing it never shows an empty section.
    'Background (Lexis)': ['Financial distress', 'Criminal & sanctions', 'Corporate status', 'Other changes'],
    'Verification (Lexis)': ['Identity verification', 'Risk & watchlists'],
};
const categoryOf = (group, signal) => {
    if (group === 'Background (Lexis)') return bgCategory(signal);
    if (group === 'Verification (Lexis)') return verCategory(signal);
    return null;
};

const ACRONYMS = new Set(['UCC', 'OOS', 'ISS', 'DEA', 'SOS', 'FMCSA', 'MCS', 'DBA', 'ZIP', 'URL', 'IRS', 'TIN', 'OFAC', 'CMRA']);
// SOS_STATUS_DEGRADED -> "Sos status degraded", then acronyms upper-cased -> keep "UCC"/"OOS"/etc. intact.
const humanize = (signal) => signal.replace(/_/g, ' ').toLowerCase().split(' ')
    .map((w, i) => (ACRONYMS.has(w.toUpperCase()) ? w.toUpperCase() : (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)))
    .join(' ');
// The report name is shown by the rail + header, so strip that redundant prefix from each signal label.
const GROUP_PREFIX = {
    FMCSA: /^FMCSA_/, 'Secretary of State': /^SOS_/, 'Verification (Lexis)': /^VERIFICATION_/, 'UCC search': /^UCC_/,
};
const labelForSignal = (signal, group) => {
    const stripped = GROUP_PREFIX[group] ? signal.replace(GROUP_PREFIX[group], '') : signal;
    return humanize(stripped || signal);
};
const titleCase = (severity) => severity.charAt(0) + severity.slice(1).toLowerCase();
const onValue = (r) => r._last || (r.defaultSeverity && r.defaultSeverity !== 'OFF' ? r.defaultSeverity : 'WARNING');

const ChevronIcon = ({ up }) => (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"
        style={{ transform: up ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
        <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const MonitoringSettings = ({
    open, onClose, getConfig, updateConfig, getFieldCatalog, getWatchedFields, updateWatchedFields,
}) => {
    const [items, setItems] = useState([]);
    const [initial, setInitial] = useState({});
    const [initialWO, setInitialWO] = useState({}); // baseline worseningOnly per signal, to detect unsaved
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [selected, setSelected] = useState(null);
    const [reportSearch, setReportSearch] = useState('');
    const [fieldSearch, setFieldSearch] = useState('');
    const [expanded, setExpanded] = useState({});   // group -> "More fields" expanded
    const [collapsed, setCollapsed] = useState({});  // `${group}::${category}` -> collapsed
    // Whether the field catalog + watched fields both loaded cleanly. When false (transient failure or a
    // not-yet-deployed catalog endpoint), Save must NOT send the empty derived list as a full replacement,
    // or it would wipe the client's existing watched fields.
    const [fieldsLoaded, setFieldsLoaded] = useState(false);
    // Whether the severity config loaded cleanly. When false, Save must NOT send the (empty) derived
    // overrides — a no-op against the current merge-based API, but a guard against silently clearing
    // overrides should the API ever become a full replacement, symmetric with fieldsLoaded.
    const [configLoaded, setConfigLoaded] = useState(false);

    useEffect(() => {
        if (!open || !getConfig) return;
        setLoading(true);
        setSaved(false);
        setFieldSearch('');
        setReportSearch('');
        setFieldsLoaded(false);
        setConfigLoaded(false);
        // Track load success separately per data set: a wired call that REJECTS marks that data unloaded
        // (so Save won't send a derived-empty replacement for it), whereas an absent fn is "feature not wired".
        let fieldsOk = true;
        let configOk = true;
        const loadConfig = (fn) => (fn
            ? Promise.resolve(fn()).catch(() => { configOk = false; return []; })
            : Promise.resolve([]));
        const loadFieldData = (fn) => (fn
            ? Promise.resolve(fn()).catch(() => { fieldsOk = false; return []; })
            : Promise.resolve([]));
        Promise.all([loadConfig(getConfig), loadFieldData(getFieldCatalog), loadFieldData(getWatchedFields)])
            .then(([config, catalog, watched]) => {
                const sig = (config || [])
                    .filter((r) => r.signal !== 'OTHER')
                    .map((r) => {
                        const group = REPORT_GROUP[r.reportType] || groupOfSignal(r.signal);
                        return {
                            kind: 'signal', id: r.signal, label: labelForSignal(r.signal, group),
                            severity: r.severity, _last: r.severity !== 'OFF' ? r.severity : undefined,
                            defaultSeverity: r.defaultSeverity, group,
                            category: categoryOf(group, r.signal),
                            directional: !!r.directional, worseningOnly: !!r.worseningOnly,
                        };
                    });
                const watchedList = watched || [];
                const fields = (catalog || []).map((f) => {
                    const w = watchedList.find((x) => x.reportType === f.reportType && x.fieldKey === f.key);
                    const severity = w ? w.severity : 'OFF';
                    return {
                        kind: 'field', id: `F:${f.reportType}:${f.key}`, label: f.label,
                        severity, _last: severity !== 'OFF' ? severity : undefined, defaultSeverity: 'OFF',
                        group: REPORT_GROUP[f.reportType] || 'Other', reportType: f.reportType, fieldKey: f.key,
                    };
                });
                const all = [...sig, ...fields];
                setItems(all);
                setInitial(Object.fromEntries(all.map((r) => [r.id, r.severity])));
                setInitialWO(Object.fromEntries(sig.map((r) => [r.id, !!r.worseningOnly])));
                setFieldsLoaded(fieldsOk);
                setConfigLoaded(configOk);
            })
            .finally(() => setLoading(false));
    }, [open, getConfig, getFieldCatalog, getWatchedFields]);

    const groups = useMemo(() => GROUP_ORDER
        .map((group) => {
            const all = items.filter((r) => r.group === group);
            return { group, total: all.length, monitored: all.filter((r) => r.severity !== 'OFF').length };
        })
        .filter((g) => g.total > 0), [items]);

    useEffect(() => {
        if (!groups.length) return;
        if (!selected || !groups.some((g) => g.group === selected)) setSelected(groups[0].group);
    }, [groups, selected]);

    if (!open) return null;

    const setSeverity = (id, severity) => {
        setItems((prev) => prev.map((r) => (r.id === id ? { ...r, severity, _last: severity } : r)));
        setSaved(false);
    };
    const setDirection = (id, worseningOnly) => {
        setItems((prev) => prev.map((r) => (r.id === id ? { ...r, worseningOnly } : r)));
        setSaved(false);
    };
    const setMonitored = (id, monitored) => {
        setItems((prev) => prev.map((r) => (r.id === id ? { ...r, severity: monitored ? onValue(r) : 'OFF' } : r)));
        setSaved(false);
    };
    const setManyMonitored = (predicate, monitored) => {
        setItems((prev) => prev.map((r) => (predicate(r) ? { ...r, severity: monitored ? onValue(r) : 'OFF' } : r)));
        setSaved(false);
    };
    const setGroupImportance = (group, severity) => {
        if (!severity) return;
        setItems((prev) => prev.map((r) => (r.group === group && r.severity !== 'OFF'
            ? { ...r, severity, _last: severity } : r)));
        setSaved(false);
    };
    const applyPreset = (preset) => {
        setItems((prev) => prev.map((r) => {
            if (preset === 'everything') return { ...r, severity: onValue(r), _last: onValue(r) };
            if (preset === 'minimal') {
                const on = r.defaultSeverity === 'ADVERSE';
                return { ...r, severity: on ? 'ADVERSE' : 'OFF', _last: on ? 'ADVERSE' : r._last };
            }
            const def = r.defaultSeverity && r.defaultSeverity !== 'OFF' ? r.defaultSeverity : 'OFF';
            return { ...r, severity: def, _last: def !== 'OFF' ? def : r._last };
        }));
        setSaved(false);
    };

    const unsaved = items.filter((r) => r.severity !== initial[r.id]
        || (r.directional && !!r.worseningOnly !== !!initialWO[r.id])).length;

    const save = async () => {
        setSaving(true);
        try {
            const overrides = {};
            const worseningOnly = {};
            items.filter((r) => r.kind === 'signal').forEach((r) => {
                overrides[r.id] = r.severity;
                if (r.directional) worseningOnly[r.id] = !!r.worseningOnly;
            });
            const watched = items
                .filter((r) => r.kind === 'field' && r.severity !== 'OFF')
                .map((r) => ({ reportType: r.reportType, fieldKey: r.fieldKey, severity: r.severity }));
            // Only send each dataset when it loaded cleanly — otherwise the derived payload is empty for the
            // wrong reason and would wipe the client's existing overrides/watched fields on the server.
            const configRan = configLoaded;
            const watchedRan = !!(updateWatchedFields && fieldsLoaded);
            await Promise.all([
                configRan ? updateConfig(overrides, worseningOnly) : Promise.resolve(),
                watchedRan ? updateWatchedFields(watched) : Promise.resolve(),
            ]);
            // Advance the baseline only for the dataset whose PUT actually ran; a skipped update keeps its
            // changes flagged unsaved rather than falsely clearing the indicator.
            setInitial((prev) => {
                const next = { ...prev };
                items.forEach((r) => {
                    if ((r.kind === 'signal' && configRan) || (r.kind === 'field' && watchedRan)) {
                        next[r.id] = r.severity;
                    }
                });
                return next;
            });
            if (configRan) {
                setInitialWO(Object.fromEntries(
                    items.filter((r) => r.kind === 'signal').map((r) => [r.id, !!r.worseningOnly])));
            }
            // Only claim "saved" when nothing was skipped: signals persisted, and fields persisted or absent.
            setSaved(configRan && (watchedRan || !updateWatchedFields));
        } finally {
            setSaving(false);
        }
    };

    const railGroups = groups.filter((g) => !reportSearch || g.group.toLowerCase().includes(reportSearch.toLowerCase()));

    const renderRow = (r, { withTag } = {}) => {
        const monitored = r.severity !== 'OFF';
        return (
            <div className={`msettings__row ${monitored ? '' : 'is-off'}`} key={r.id}>
                <label className="msettings__toggle">
                    <input type="checkbox" checked={monitored} onChange={(e) => setMonitored(r.id, e.target.checked)} />
                    <span className="msettings__signal">
                        {r.label}
                        {withTag && <span className="msettings__reportTag">{r.group}</span>}
                    </span>
                </label>
                {monitored ? (
                    <span className="msettings__importance">
                        {r.directional && (
                            <select
                                className="msettings__select msettings__direction"
                                value={r.worseningOnly ? 'WORSENING' : 'BOTH'}
                                onChange={(e) => setDirection(r.id, e.target.value === 'WORSENING')}
                                title="Which direction to alert on"
                            >
                                <option value="BOTH">Both directions</option>
                                <option value="WORSENING">Worsening only</option>
                            </select>
                        )}
                        <span className={`msettings__dot sev-${(r.severity || '').toLowerCase()}`} />
                        <select className="msettings__select" value={r.severity} title="How important this change is"
                            onChange={(e) => setSeverity(r.id, e.target.value)}>
                            {IMPORTANCE.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
                        </select>
                    </span>
                ) : (
                    <span className="msettings__notMonitored">Not monitored</span>
                )}
            </div>
        );
    };

    const searching = fieldSearch.trim().length > 0;
    const searchHits = searching
        ? items.filter((r) => r.label.toLowerCase().includes(fieldSearch.trim().toLowerCase()))
        : [];

    const active = groups.find((g) => g.group === selected);
    const groupSignals = items.filter((r) => r.group === selected && r.kind === 'signal');
    const groupFields = items.filter((r) => r.group === selected && r.kind === 'field');
    const cats = CATEGORIES[selected];

    // Category sections (Background, Verification) or a single "Key signals" section (everything else).
    const signalSections = cats
        ? cats
            .map((cat) => ({ title: cat, key: cat, sigs: groupSignals.filter((r) => r.category === cat) }))
            .filter((s) => s.sigs.length)
        : (groupSignals.length ? [{ title: groupFields.length ? 'Key signals' : null, key: 'key', sigs: groupSignals }] : []);

    const monitoredIn = (sigs) => sigs.filter((r) => r.severity !== 'OFF').length;
    const isExpanded = !!expanded[selected];

    return (
        <>
            <div className="msettings__backdrop" onClick={onClose} />
            <aside className="msettings">
                <div className="msettings__head">
                    <div>
                        <h3 className="msettings__title">Monitoring configuration</h3>
                        <p className="msettings__intro">Choose which changes each report is watched for, and how important each one is.</p>
                    </div>
                    <button className="msettings__close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="msettings__presets">
                    <span className="msettings__presetsLabel">Presets</span>
                    {[['recommended', 'Recommended'], ['everything', 'Everything'], ['minimal', 'Minimal']]
                        .map(([key, label]) => <button key={key} className="msettings__preset" onClick={() => applyPreset(key)}>{label}</button>)}
                </div>

                {loading && <div className="msettings__loading">Loading…</div>}

                {!loading && (
                    <div className="msettings__body">
                        <div className="msettings__rail">
                            <div className="msettings__search">
                                <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
                                    <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                                    <path d="M10.5 10.5 14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                </svg>
                                <input placeholder="Search all changes…" value={fieldSearch} onChange={(e) => setFieldSearch(e.target.value)} />
                            </div>
                            <input className="msettings__reportFilter" placeholder="Filter reports" value={reportSearch}
                                onChange={(e) => setReportSearch(e.target.value)} />
                            <div className="msettings__railList">
                                {railGroups.map((g) => (
                                    <button key={g.group}
                                        className={`msettings__railItem ${!searching && g.group === selected ? 'is-active' : ''}`}
                                        onClick={() => { setSelected(g.group); setFieldSearch(''); }}>
                                        <span>{g.group}</span>
                                        <span className="msettings__railCount">{g.monitored}/{g.total}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="msettings__main">
                            {searching ? (
                                <>
                                    <div className="msettings__mainHead">
                                        <span className="msettings__mainTitle">Search results</span>
                                        <span className="msettings__mainMeta">{searchHits.length} match{searchHits.length === 1 ? '' : 'es'}</span>
                                    </div>
                                    {searchHits.length === 0
                                        ? <div className="msettings__empty">No changes match “{fieldSearch}”.</div>
                                        : searchHits.map((r) => renderRow(r, { withTag: true }))}
                                </>
                            ) : active ? (
                                <>
                                    <div className="msettings__mainHead">
                                        <span className="msettings__mainTitle">{active.group}</span>
                                        <span className="msettings__mainMeta">{active.monitored}/{active.total} monitored</span>
                                        <div className="msettings__mainActions">
                                            <button className="msettings__linkBtn" onClick={() => setManyMonitored((r) => r.group === selected, true)}>Turn all on</button>
                                            <span className="msettings__sep" />
                                            <button className="msettings__linkBtn" onClick={() => setManyMonitored((r) => r.group === selected, false)}>Turn all off</button>
                                            <select className="msettings__select msettings__setAll" value=""
                                                title="Set importance for all monitored changes in this report"
                                                onChange={(e) => { setGroupImportance(selected, e.target.value); e.target.value = ''; }}>
                                                <option value="">Set all importance to…</option>
                                                {IMPORTANCE.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    {signalSections.map((section) => {
                                        const catKey = `${selected}::${section.key}`;
                                        const isCollapsed = !!collapsed[catKey];
                                        const collapsible = !!cats;
                                        return (
                                            <div key={section.key} className="msettings__section">
                                                {section.title && (collapsible ? (
                                                    <button className="msettings__catHead"
                                                        onClick={() => setCollapsed((p) => ({ ...p, [catKey]: !p[catKey] }))}>
                                                        <ChevronIcon up={!isCollapsed} />
                                                        <span className="msettings__catTitle">{section.title}</span>
                                                        <span className="msettings__catCount">{monitoredIn(section.sigs)}/{section.sigs.length}</span>
                                                    </button>
                                                ) : (
                                                    <div className="msettings__subhead">{section.title} <span>{monitoredIn(section.sigs)}/{section.sigs.length}</span></div>
                                                ))}
                                                {!(collapsible && isCollapsed) && section.sigs.map((r) => renderRow(r))}
                                            </div>
                                        );
                                    })}

                                    {groupFields.length > 0 && (
                                        <div className="msettings__section">
                                            <div className="msettings__subhead">More fields <span>{monitoredIn(groupFields)}/{groupFields.length}</span></div>
                                            {(isExpanded ? groupFields : []).map((r) => renderRow(r))}
                                            <button className="msettings__moreBtn" onClick={() => setExpanded((p) => ({ ...p, [selected]: !p[selected] }))}>
                                                <ChevronIcon up={isExpanded} />
                                                {isExpanded ? 'Hide fields' : `Show ${groupFields.length} more field${groupFields.length === 1 ? '' : 's'}`}
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="msettings__empty">No monitorable changes available.</div>
                            )}
                        </div>
                    </div>
                )}

                <div className="msettings__actions">
                    <span className="msettings__status">
                        {unsaved > 0 && <span className="msettings__unsaved">{unsaved} unsaved</span>}
                        {unsaved === 0 && saved && (
                            <span className="msettings__saved">
                                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                                    <path d="M13.5 4.5 6.5 11.5 3 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                Saved
                            </span>
                        )}
                    </span>
                    <button className="msettings__cancel" onClick={onClose}>Cancel</button>
                    <button className="msettings__save" disabled={unsaved === 0 || saving} onClick={save}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </aside>
        </>
    );
};

export default MonitoringSettings;
